import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, PageError, PageLoading } from "../components/index.js";
import { Field } from "../components/AdminForm.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { BillingTabs, ProductOrdersBilling } from "../components/productOrders/ProductOrdersBilling.jsx";
import { billingTabParams } from "../lib/productOrders.js";

/**
 * Billing desk (Phase 4c). Prism schedules; QuickBooks Desktop invoices.
 * Tabs: To invoice · Awaiting payment · Paid (90d) · All · Product orders.
 *
 * AgencyBilling is a dispatcher: the Product orders tab mounts
 * ProductOrdersBilling (Task 6) INSTEAD of the contract desk, so the contract
 * hooks below never run for it and no product filter can reach
 * /api/billing/items. Everything else is ContractBilling — the desk exactly
 * as it was: its tabs, totals, queries, rows and actions are unchanged. Row
 * actions move a BillingItem through scheduled → invoiced → paid (or void),
 * release a milestone, or enter an amount. Every action reloads the list +
 * summary. A tab switch carries only the tab and the client (billingTabParams).
 */
export function AgencyBilling() {
  const [params] = useSearchParams();
  return params.get("tab") === "product_orders" ? <ProductOrdersBilling /> : <ContractBilling />;
}

const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC" }) : "—");
const todayStr = () => new Date().toISOString().slice(0, 10);

const CHIP = {
  scheduled:     { bg: "#f5f5f5", fg: "#525252", label: "Scheduled" },
  needsAmount:   { bg: "#fbeae0", fg: "#8a4b1a", label: "Needs amount" },
  awaitRelease:  { bg: "#fbeae0", fg: "#8a4b1a", label: "Awaiting release" },
  invoiced:      { bg: "#eff6ff", fg: "#2563eb", label: "Invoiced" },
  overdue:       { bg: "#fef2f2", fg: "#dc2626", label: "Overdue" },
  paid:          { bg: "#f0fdf4", fg: "#16a34a", label: "Paid" },
  void:          { bg: "#f5f5f5", fg: "#a3a3a3", label: "Void" },
};
export function chipKey(item) {
  if (item.status === "scheduled") return item.awaitingRelease ? "awaitRelease" : item.needsAmount ? "needsAmount" : "scheduled";
  if (item.status === "invoiced") return item.isOverdue ? "overdue" : "invoiced";
  return item.status;
}
export function BillingChip({ item }) {
  const c = CHIP[chipKey(item)] ?? CHIP.scheduled;
  return <span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.fg }}>{c.label}{item.isOverdue ? ` · ${item.overdueDays}d` : ""}</span>;
}

function ContractBilling() {
  const { user } = useAuth();
  const superAdmin = user?.role === "SUPER_ADMIN";
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "to_invoice";
  const clientId = params.get("clientId") || "";
  const month = params.get("month") || "";
  const sort = params.get("sort") || ""; // "" = the tab's default (asc; paid desc)
  const page = Number(params.get("page") || "1");
  const qs = new URLSearchParams({ tab, page: String(page), ...(clientId ? { clientId } : {}), ...(sort ? { sort } : {}), ...(month && tab === "all" ? { month } : {}) }).toString();
  const { data, loading, error, reload } = useApi(`/api/billing/items?${qs}`);
  const clientsApi = useApi("/api/me/clients");
  const api = useApiAction();
  const [modal, setModal] = useState(null); // { kind, item }
  const [actionError, setActionError] = useState(null);

  function setParam(k, v) { setParams((p) => { if (v) p.set(k, v); else p.delete(k); return p; }, { replace: true }); }
  // Changing a filter invalidates the current page — always land back on page 1.
  function setFilter(k, v) { setParams((p) => { if (v) p.set(k, v); else p.delete(k); p.delete("page"); if (k === "tab") p.delete("sort"); return p; }, { replace: true }); }

  async function act(path, body, method = "POST") {
    setActionError(null);
    try { await api(path, { method, body: JSON.stringify(body ?? {}) }); setModal(null); await reload({ silent: true }); }
    catch (e) { setActionError(e.message); throw e; }
  }

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading && !data) return <PageLoading />;
  const items = data?.items || [];
  const s = data?.summary || {};
  const clients = (clientsApi.data?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="max-w-[1200px] space-y-4">
      <div className="text-[11px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>Contract billing</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["To invoice", s.toInvoice, s.toInvoiceCount], ["Outstanding", s.outstanding, null], ["Overdue", s.overdue, s.overdueCount]].map(([label, total, count]) => (
          <Card key={label}><div className="px-4 py-3"><div className="text-[11px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>{label}</div><div className="text-[20px] font-semibold text-text-primary">{fmtMoney(total)}</div>{count != null && <div className="text-[12px] text-text-secondary">{count} item{count === 1 ? "" : "s"}</div>}</div></Card>
        ))}
        <Card><div className="px-4 py-3 text-[12px] text-text-secondary">Prism schedules; QuickBooks Desktop invoices. Mark each item invoiced with its QuickBooks number, then paid when the money lands.</div></Card>
      </div>

      <Card className="p-0">
        <div className="px-4 py-2.5 border-b border-border-light flex flex-wrap items-center gap-2">
          <BillingTabs active={tab} onChange={(key) => setParams(billingTabParams(key, clientId), { replace: true })} />
          <span className="flex-1" />
          <select value={clientId} onChange={(e) => setFilter("clientId", e.target.value)} className="text-[12px] border border-border-default rounded px-2 py-1 bg-surface">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {tab === "all" && <input type="month" value={month} onChange={(e) => setFilter("month", e.target.value)} className="text-[12px] border border-border-default rounded px-2 py-1 bg-surface" />}
        </div>
        {actionError && <div className="px-4 py-2 text-[12px] text-negative border-b border-border-light">{actionError}</div>}
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-text-muted">Nothing here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] uppercase text-text-muted" style={{ letterSpacing: "0.5px" }}>
                {["Client", "Service", "Period", "Due", "Amount", "Status", "QB #", ""].map((h) => h === "Due" ? (
                  <th key={h} className="text-left font-medium px-4 py-2"><button type="button" onClick={() => setParam("sort", (sort || (tab === "paid" ? "desc" : "asc")) === "asc" ? "desc" : "asc")} className="uppercase hover:text-text-primary" title="Sort by date">{tab === "awaiting" ? "Invoiced" : tab === "paid" ? "Paid" : "Due"} {(sort || (tab === "paid" ? "desc" : "asc")) === "asc" ? "↑" : "↓"}</button></th>
                ) : <th key={h} className="text-left font-medium px-4 py-2">{h}</th>)}
              </tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-border-light">
                    <td className="px-4 py-2 font-medium text-text-primary">{it.client?.name}</td>
                    <td className="px-4 py-2">{it.serviceName}</td>
                    <td className="px-4 py-2 font-mono text-[12px]">{it.periodKey}</td>
                    <td className="px-4 py-2">{fmtDate(it.dueDate)}</td>
                    <td className="px-4 py-2">{fmtMoney(it.amount)}{it.quantity != null ? <span className="text-text-muted"> ×{it.quantity}</span> : null}</td>
                    <td className="px-4 py-2"><BillingChip item={it} /></td>
                    <td className="px-4 py-2 font-mono text-[12px]">{it.qbInvoiceNumber || "—"}{it.paidAt ? <span className="text-text-muted"> · paid {fmtDate(it.paidAt)}</span> : null}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      <RowActions item={it} superAdmin={superAdmin} onOpen={(kind) => {
                        if (kind !== "undo") return setModal({ kind, item: it });
                        const back = it.status === "paid" ? "invoiced" : "scheduled";
                        if (confirm(`Undo this step? The item goes back to ${back}.${it.status === "paid" ? " Undo once more to reach scheduled, where it can be edited." : ""} This is logged.`)) act(`/api/billing/items/${it.id}/undo`).catch(() => {});
                      }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.total > 0 && (
          <div className="px-4 py-2 flex items-center justify-between text-[12px] text-text-muted border-t border-border-light">
            <span>Showing {(page - 1) * data.pageSize + Math.min(1, items.length)}–{(page - 1) * data.pageSize + items.length} of {data.total}</span>
            <span className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="px-2 py-1 rounded border border-border-default disabled:opacity-40">Prev</button>
              <button type="button" disabled={page * data.pageSize >= data.total} onClick={() => setParam("page", String(page + 1))} className="px-2 py-1 rounded border border-border-default disabled:opacity-40">Next</button>
            </span>
          </div>
        )}
      </Card>

      {modal && <ActionModal modal={modal} onClose={() => setModal(null)} onAct={act} />}
    </div>
  );
}

function RowActions({ item, superAdmin, onOpen }) {
  const link = (label, kind, cls = "text-accent") => <button type="button" onClick={() => onOpen(kind)} className={`text-[12px] ${cls} hover:underline ml-3`}>{label}</button>;
  if (item.status === "scheduled") {
    return (
      <>
        {item.awaitingRelease && link("Release", "release")}
        {link("Edit", "edit", "text-text-muted")}
        {!item.awaitingRelease && !item.needsAmount && link("Mark invoiced", "invoiced")}
        {link("Void", "void", "text-text-muted")}
      </>
    );
  }
  if (item.status === "invoiced") return <>{link("Mark paid", "paid")}{superAdmin && link("Void", "void", "text-text-muted")}{superAdmin && link("Undo", "undo", "text-text-muted")}</>;
  if ((item.status === "paid" || item.status === "void") && superAdmin) return link("Undo", "undo", "text-text-muted");
  return null;
}

function ActionModal({ modal, onClose, onAct }) {
  const { kind, item } = modal;
  const [qb, setQb] = useState("");
  const [date, setDate] = useState(kind === "edit" ? (item.dueDate ?? "") : todayStr());
  const [note, setNote] = useState(item.note ?? "");
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(item.amount ?? "");
  const [quantity, setQuantity] = useState(item.quantity ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const title = { invoiced: "Mark invoiced", paid: "Mark paid", void: "Void item", release: "Release milestone", edit: "Edit item" }[kind];
  async function submit() {
    setBusy(true); setErr(null);
    try {
      if (kind === "invoiced") await onAct(`/api/billing/items/${item.id}/invoiced`, { qbInvoiceNumber: qb.trim(), invoicedAt: date });
      else if (kind === "paid") await onAct(`/api/billing/items/${item.id}/paid`, { paidAt: date });
      else if (kind === "void") await onAct(`/api/billing/items/${item.id}/void`, { reason: reason.trim() });
      else if (kind === "release") await onAct(`/api/billing/items/${item.id}/release`, { dueDate: date });
      else if (kind === "edit") {
        const body = { amount: amount === "" ? null : Number(amount), note: note.trim() || null };
        if (item.kind === "per_unit") body.quantity = quantity === "" ? null : Number(quantity);
        if (!item.awaitingRelease) body.dueDate = date || null;
        await onAct(`/api/billing/items/${item.id}`, body, "PUT");
      }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  const ready = kind === "invoiced" ? qb.trim().length > 0 : kind === "void" ? reason.trim().length > 0 : kind === "edit" ? (amount === "" || Number(amount) >= 0) : true;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[420px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-1">{title}</h2>
        <p className="text-[12px] text-text-muted mb-3">{item.client?.name} · {item.serviceName} · {item.periodKey}{item.amount != null ? ` · ${fmtMoney(item.amount)}` : ""}</p>
        <div className="space-y-2.5">
          {kind === "invoiced" && <Field label="QuickBooks invoice #" required value={qb} onChange={setQb} autoFocus />}
          {kind === "edit" && <Field label="Amount (USD)" type="number" value={amount} onChange={setAmount} autoFocus help={item.kind === "recurring" ? "Overrides this period only; the planner keeps it." : undefined} />}
          {kind === "edit" && item.kind === "per_unit" && <Field label="Quantity" type="number" value={quantity} onChange={setQuantity} />}
          {kind === "edit" && !item.awaitingRelease && <Field label="Due date" type="date" value={date} onChange={setDate} />}
          {kind === "edit" && <Field label="Note" value={note} onChange={setNote} placeholder="Optional - e.g. partial payment received" />}
          {["invoiced", "paid", "release"].includes(kind) && <Field label={kind === "release" ? "Due date" : kind === "paid" ? "Paid on" : "Invoiced on"} type="date" required value={date} onChange={setDate} />}
          {kind === "void" && <Field label="Reason" required value={reason} onChange={setReason} autoFocus />}
        </div>
        {err && <p className="text-[12px] text-negative mt-2">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-50">Cancel</button>
          <button type="button" disabled={!ready || busy} onClick={submit} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{busy ? "Saving…" : title}</button>
        </div>
      </div>
    </div>
  );
}
